import { useLazyQuery, useQuery } from "@apollo/client";
import { useContext } from "react";
import { GlobalContext } from "~/context/global-context";
import { graphql } from "~/gql";
import { FilteredMattersInput } from "~/gql/graphql";
import { MatterStatus } from "~/graphql/__generated_types__/globalTypes";

type BaseFilterHelperResult = {
  vars: FilteredMattersInput;
  withNewStatus: (status: MatterStatus) => FilteredMattersInput;
};
export function getBaseFilterVariables(): BaseFilterHelperResult {
  const currentUserInfo = useContext(GlobalContext);

  const baseVariables: FilteredMattersInput = {
    filter: {
      status: [MatterStatus.Draft],
      template: false,
    },
    orderBy: [
      {
        updatedAt: "desc",
      },
    ],
    offset: 0,
    limit: 10,
  };

  return {
    vars: {
      ...baseVariables,
      filter: {
        ...baseVariables.filter,
        ...(currentUserInfo?.currentUser?.userId && {
          createdBy: [currentUserInfo?.currentUser.userId],
        }),
      },
    },
    withNewStatus: (status: MatterStatus) => {
      const result: FilteredMattersInput = {
        ...baseVariables,
        filter: {
          ...baseVariables.filter,
          status: [status],
          ...(currentUserInfo?.currentUser?.userId && {
            createdById: { in: [currentUserInfo?.currentUser.userId] },
          }),
        },
      };

      return result;
    },
  };
}

// filter: {
//   ...(filter?.title && { title: { contains: filter.title, mode: QueryMode.Insensitive } }),
//   ...(filter?.client && { client: { is: { id: { in: filter.client } } } }),
//   ...(filter?.status && { status: { in: filter.status } }),
//   ...(filter?.updatedAt && {
//     updatedAt: { gte: filter.updatedAt.from, lte: filter.updatedAt.to },
//   }),
//   ...(filter?.createdBy && { createdById: { in: filter.createdBy } }),
//   template: { equals: false },
// },

export type FilteredMattersQueryVariables = {
  title?: string;
  clientIds?: number[];
  status?: MatterStatus[];
  updatedAt: {
    from: string;
    to: string;
  };
  createdBy?: number[];
};

export const useFilteredMattersQuery = (
  filteredMattersProps: FilteredMattersInput,
  skip?: boolean
) => {
  const queryTypes = useQuery(
    graphql(/* GraphQL */ `
      query FilterMatters($input: FilteredMattersInput!) {
        filteredMatters(input: $input) {
          count
          matters {
            __typename
            reference
            id
            isParent
            title
            createdAt
            client {
              id
              name
              externalReference
              type
            }
            organisationalUnit {
              __typename
              id
              name
              parent {
                id
                name
              }
            }
            updatedAt
            status
            createdAt
            createdBy {
              id
              userName
              displayName
            }
            externalMatterId
            externalReference
            currency {
              id
              code
            }
          }
        }
      }
    `),
    {
      variables: {
        input: filteredMattersProps,
      },
      skip,
      fetchPolicy: "cache-and-network",
    }
  );

  return queryTypes;
};

export const useFilteredMattersLazyQuery = (filteredMattersProps: FilteredMattersInput) => {
  const [callFunc, { loading, data, variables, error, fetchMore: fetchMoreRaw }] = useLazyQuery(
    graphql(/* GraphQL */ `
      query FilterMatters($input: FilteredMattersInput!) {
        filteredMatters(input: $input) {
          count
          matters {
            __typename
            reference
            id
            isParent
            title
            createdAt
            client {
              id
              name
              externalReference
              type
            }
            organisationalUnit {
              __typename
              id
              name
              parent {
                id
                name
              }
            }
            updatedAt
            status
            createdAt
            createdBy {
              id
              userName
              displayName
            }
            externalMatterId
            externalReference
            currency {
              id
              code
            }
          }
        }
      }
    `),
    {
      variables: {
        input: filteredMattersProps,
      },
      fetchPolicy: "cache-and-network",
    }
  );

  const total = data?.filteredMatters?.count || 0;

  return {
    callFunc,
    loading,
    data,
    variables,
    error,
    total,
    fetchMore: () => {
      fetchMoreRaw &&
        fetchMoreRaw({
          variables: {
            input: {
              ...variables?.input,
              offset: data?.filteredMatters?.matters?.length || 0,
              limit: 10,
            },
          },
          updateQuery(prev, { fetchMoreResult }) {
            if (!fetchMoreResult) return prev;
            return {
              ...prev,
              filteredMatters: {
                ...prev.filteredMatters,
                count: fetchMoreResult.filteredMatters.count,
                matters: [
                  ...prev.filteredMatters.matters,
                  ...fetchMoreResult.filteredMatters.matters,
                ],
              },
            };
          },
        });
    },
  };
};
